type IntervalNode<T> = {
  low: number;
  high: number;
  value: T;
  left: IntervalNode<T> | null;
  right: IntervalNode<T> | null;
};

export type Interval<T> = { low: number; high: number; value: T };

/**
 * Set of disjoint, closed intervals [low, high] each carrying a payload, kept
 * coalesced. Adding a range that overlaps or touches existing ranges with the
 * SAME payload merges them all into one; overlapping/touching a range with a
 * DIFFERENT payload is rejected.
 *
 * add: O(h + k) for k merged intervals; find / overlaps: O(h). The set stays
 * disjoint, so every interval has a unique `low`. Not self-balancing — h is
 * O(log n) for random input, O(n) worst case.
 */
export class IntervalTree<T> {
  private root: IntervalNode<T> | null = null;

  /** Adds, or returns false (without mutating) if it would conflict. One pass. */
  public tryAdd(low: number, high: number, value: T): boolean {
    if (high < low) return false;
    return this.attemptAdd(low, high, value) === null;
  }

  public add(low: number, high: number, value: T): void {
    if (high < low) {
      throw new Error(`invalid interval: high (${high}) < low (${low})`);
    }
    const conflict = this.attemptAdd(low, high, value);
    if (conflict) {
      throw new Error(
        `interval [${low}, ${high}] overlaps [${conflict.low}, ${conflict.high}] ` +
          `with a different payload`
      );
    }
  }

  // Returns the conflicting interval (no mutation), or null after merging in.
  private attemptAdd(low: number, high: number, value: T): Interval<T> | null {
    const touching: IntervalNode<T>[] = [];
    this.collectTouching(this.root, low, high, touching);

    const clash = touching.find(node => node.value !== value);
    if (clash) {
      return { low: clash.low, high: clash.high, value: clash.value };
    }

    let mergedLow = low;
    let mergedHigh = high;
    for (const node of touching) {
      mergedLow = Math.min(mergedLow, node.low);
      mergedHigh = Math.max(mergedHigh, node.high);
      this.root = this.removeByLow(this.root, node.low);
    }
    this.root = this.insertNode(this.root, mergedLow, mergedHigh, value);
    return null;
  }

  /** Whether add(low, high, value) would succeed (no different-payload conflict). */
  public canAdd(low: number, high: number, value: T): boolean {
    if (high < low) return false;
    return !this.hasConflict(this.root, low, high, value);
  }

  /** The interval containing `point`, or null. */
  public find(point: number): Interval<T> | null {
    let node = this.root;
    while (node) {
      if (point < node.low) node = node.left;
      else if (point > node.high) node = node.right;
      else return { low: node.low, high: node.high, value: node.value };
    }
    return null;
  }

  public contains(point: number): boolean {
    return this.find(point) !== null;
  }

  /** Whether any stored interval overlaps or touches [low, high]. */
  public overlaps(low: number, high: number): boolean {
    return this.anyOverlapping(this.root, low, high);
  }

  /** All intervals, ascending by low. */
  public toArray(): Interval<T>[] {
    const out: Interval<T>[] = [];
    this.inorder(this.root, out);
    return out;
  }

  private collectTouching(
    node: IntervalNode<T> | null,
    low: number,
    high: number,
    out: IntervalNode<T>[]
  ): void {
    if (!node) return;
    if (node.high < low) {
      this.collectTouching(node.right, low, high, out);
      return;
    }
    if (node.low > high) {
      this.collectTouching(node.left, low, high, out);
      return;
    }
    this.collectTouching(node.left, low, high, out);
    out.push(node);
    this.collectTouching(node.right, low, high, out);
  }

  private anyOverlapping(
    node: IntervalNode<T> | null,
    low: number,
    high: number
  ): boolean {
    if (!node) return false;
    if (node.high < low) return this.anyOverlapping(node.right, low, high);
    if (node.low > high) return this.anyOverlapping(node.left, low, high);
    return true;
  }

  private hasConflict(
    node: IntervalNode<T> | null,
    low: number,
    high: number,
    value: T
  ): boolean {
    if (!node) return false;
    if (node.high < low) return this.hasConflict(node.right, low, high, value);
    if (node.low > high) return this.hasConflict(node.left, low, high, value);
    if (node.value !== value) return true;
    return (
      this.hasConflict(node.left, low, high, value) ||
      this.hasConflict(node.right, low, high, value)
    );
  }

  private insertNode(
    node: IntervalNode<T> | null,
    low: number,
    high: number,
    value: T
  ): IntervalNode<T> {
    if (!node) return { low, high, value, left: null, right: null };
    if (low < node.low) {
      node.left = this.insertNode(node.left, low, high, value);
    } else {
      node.right = this.insertNode(node.right, low, high, value);
    }
    return node;
  }

  private removeByLow(
    node: IntervalNode<T> | null,
    low: number
  ): IntervalNode<T> | null {
    if (!node) return null;
    if (low < node.low) {
      node.left = this.removeByLow(node.left, low);
    } else if (low > node.low) {
      node.right = this.removeByLow(node.right, low);
    } else {
      if (!node.left) return node.right;
      if (!node.right) return node.left;
      let successor = node.right;
      while (successor.left) successor = successor.left;
      node.low = successor.low;
      node.high = successor.high;
      node.value = successor.value;
      node.right = this.removeByLow(node.right, successor.low);
    }
    return node;
  }

  private inorder(node: IntervalNode<T> | null, out: Interval<T>[]): void {
    if (!node) return;
    this.inorder(node.left, out);
    out.push({ low: node.low, high: node.high, value: node.value });
    this.inorder(node.right, out);
  }
}
